import React, { useState, useRef, useMemo, useEffect } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import API from "../api";
import toastr from "toastr";

interface Doctor {
  id: number;
  d_name: string;
}

interface IncomeRecord {
  id: number;
  entry_date: string;
  P_name: string;
  bill_no: string;
  payment_method: string;
  d_name?: string;
  total_amount: number;
}

const IncomeByReference: React.FC = () => {
  const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  };

  const getMonthEnd = () => {
    const d = new Date();
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  };

  const [startDate, setStartDate] = useState<string>(getMonthStart);
  const [endDate, setEndDate] = useState<string>(getMonthEnd);
  const [reference, setReference] = useState<string>("Self");
  const [doctors, setDoctors] = useState<Doctor[]>([]); // ✅ Fixed: was useState<<Doctor[]>([])
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const tableRef = useRef<any>(null);

  // Fetch doctors list on mount
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await API.get("/finance/getDoctors");
        setDoctors(res.data.data || []);
      } catch (err: any) {
        console.error("Failed to fetch doctors:", err);
        toastr.error("Failed to load doctors list");
      }
    };
    fetchDoctors();
  }, []);

  // Refetch table when reference changes
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.refetch();
    }
  }, [reference, startDate, endDate]);

  const fetchIncomeData = async (
    page: number,
    search: string,
    sortBy = "entry_date",
    sortOrder = "desc"
  ) => {
    try {
      const res = await API.get(
        `/finance/getIncomeByReference?page=${page}&pageSize=25&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}&startDate=${startDate}&endDate=${endDate}&reference=${reference}`
      );

      const rawData = res.data.data || [];
      const offset = (page - 1) * 25;
      const data = rawData.map((item: IncomeRecord, idx: number) => ({
        ...item,
        rowNumber: offset + idx + 1,
      }));

      const total = Number(res.data.totalAmount || 0);
      setTotalAmount(total);

      return { data, total: res.data.total };
    } catch (err: any) {
      console.error("fetchIncomeData error:", err);
      toastr.error(err?.response?.data?.message || "Failed to fetch income data");
      return { data: [], total: 0 };
    }
  };

  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    if (new Date(endDate) < new Date(startDate)) {
      toastr.warning("End date should be greater than Start date");
      return;
    }
    tableRef.current?.refetch();
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchAllIncomeByReference = async () => {
    const res = await API.get(
      `/finance/getIncomeByReference?page=1&pageSize=100000&startDate=${startDate}&endDate=${endDate}&reference=${reference}`
    );
    return res.data.data || [];
  };

  const getReferenceLabel = () => {
    if (reference === "0" || reference === "All") return "All";
    if (reference === "Self") return "Self";
    const doc = doctors.find(
      (d) => String(d.id) === String(reference) || d.d_name === reference
    );
    return doc ? doc.d_name : reference;
  };

  const handlePrint = async () => {
    try {
      const rows = await fetchAllIncomeByReference();
      const totalAmount = rows.reduce(
        (sum: number, item: IncomeRecord) => sum + Number(item.total_amount || 0),
        0
      );
      const bodyRows = rows
        .map((item: IncomeRecord, idx: number) => {
          const dateStr = item.entry_date
            ? new Date(item.entry_date).toLocaleDateString('en-GB')
            : '-';
          return `
            <tr>
              <td>${idx + 1}</td>
              <td>${dateStr}</td>
              <td>${item.P_name || ''}</td>
              <td>${item.bill_no || ''}</td>
              <td>${item.payment_method || ''}</td>
              <td>${item.d_name || 'Self'}</td>
              <td style="text-align:right">Rs. ${Number(item.total_amount || 0).toFixed(2)}</td>
            </tr>`;
        })
        .join('');

      const html = `
        <html>
          <head>
            <title>Income By Reference</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2, p { text-align: center; margin: 4px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 12px; }
              th, td { border: 1px solid #333; padding: 6px 8px; font-size: 12px; }
              th { background: #f0f0f0; }
              tfoot td { font-weight: bold; }
            </style>
          </head>
          <body>
            <h2>Income By Reference</h2>
            <p>Reference: ${getReferenceLabel()}</p>
            <p>From ${startDate} to ${endDate}</p>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Bill No</th>
                  <th>Payment Method</th>
                  <th>Referred By</th>
                  <th style="text-align:right">Amount</th>
                </tr>
              </thead>
              <tbody>${bodyRows}</tbody>
              <tfoot>
                <tr>
                  <td colspan="6" style="text-align:right">Total</td>
                  <td style="text-align:right">Rs. ${totalAmount.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
            <script>
              window.onload = function() { window.print(); };
            </script>
          </body>
        </html>`;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
      } else {
        toastr.warning('Please allow popups to print the report');
      }
    } catch (err: any) {
      console.error('handlePrint error:', err);
      toastr.error('Failed to generate print view');
    }
  };

  const handlePDF = async () => {
    try {
      const res = await API.get(
        `/finance/exportIncomeByReferencePDF?startDate=${startDate}&endDate=${endDate}&reference=${reference}`,
        { responseType: 'blob' }
      );
      downloadBlob(
        res.data,
        `income-by-reference-${encodeURIComponent(getReferenceLabel())}-${startDate}-to-${endDate}.pdf`
      );
    } catch (err: any) {
      console.error('handlePDF error:', err);
      toastr.error('Failed to generate PDF');
    }
  };

  const handleExcel = async () => {
    try {
      const res = await API.get(
        `/finance/exportIncomeByReferenceExcel?startDate=${startDate}&endDate=${endDate}&reference=${reference}`,
        { responseType: 'blob' }
      );
      downloadBlob(
        res.data,
        `income-by-reference-${encodeURIComponent(getReferenceLabel())}-${startDate}-to-${endDate}.xlsx`
      );
    } catch (err: any) {
      console.error('handleExcel error:', err);
      toastr.error('Failed to generate Excel file');
    }
  };

  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        header: "#",
        accessorKey: "rowNumber",
        cell: ({ row }: any) => row.original.rowNumber,
      },
      {
        header: "Date",
        accessorKey: "entry_date",
        cell: ({ row }: any) => {
          const date = row.original.entry_date;
          return date ? new Date(date).toLocaleDateString("en-GB") : "-";
        },
      },
      { header: "Patient", accessorKey: "P_name" },
      { header: "Bill No", accessorKey: "bill_no" },
      { header: "Payment Method", accessorKey: "payment_method" },
    ];

    if (reference === "0") {
      baseColumns.push({
        header: "Referred By",
        accessorKey: "d_name",
        cell: ({ row }: any) => row.original.d_name || "Self",
      });
    }

    baseColumns.push({
      header: "Amount",
      accessorKey: "total_amount",
      cell: ({ row }: any) =>
        `Rs. ${Number(row.original.total_amount || 0).toFixed(2)}`,
    });

    return baseColumns;
  }, [reference]);

  return (
    <div>
      <ContentHeader title="Income By Reference" />

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <form
                  onSubmit={handleDateFilter}
                  className="d-flex gap-2 align-items-center flex-wrap"
                >
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: "150px" }}
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ width: "150px" }}
                  />
                  <select
                    className="form-control"
                    value={reference}
                    onChange={(e) => {
                      setReference(e.target.value);
                      setTimeout(() => tableRef.current?.refetch(), 100);
                    }}
                    style={{ minWidth: "220px", maxWidth: "340px" }}
                  >
                    <option value="0">All</option>
                    <option value="Self">Self</option>
                    {doctors.map((doc) => (
                      <option key={doc.id} value={String(doc.id)}>
                        {doc.d_name}
                      </option>
                    ))}
                  </select>
                  <button type="submit" className="btn btn-light" title="Search">
                    <em className="fa fa-search" />
                  </button>
                </form>

                <div className="btn-group">
                  <button className="btn btn-light" onClick={handlePrint} title="Print">
                    <em className="fa fa-print" /> Print
                  </button>
                  <button className="btn btn-light" onClick={handlePDF} title="PDF">
                    <em className="fa fa-file-pdf" /> PDF
                  </button>
                  <button className="btn btn-light" onClick={handleExcel} title="Excel">
                    <em className="fa fa-file-excel" /> Excel
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchIncomeData}
                queryKey={["income-by-reference", startDate, endDate, reference]}
              />
            </div>

            <div className="card-footer bg-light">
              <div className="row">
                <div className="col-12 d-flex justify-content-end align-items-center">
                  <span className="fw-bold me-2">Total:</span>
                  <span className="fw-bold text-primary">
                    Rs. {totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IncomeByReference;